// src/Components/Partials/AchievementToast.tsx
// In-game achievement celebration. Drops in top-centre under the shell HUD:
// a gold medal with turning light rays and a category icon, a shine sweep
// across the card, the reward popping in, a few sparkles, and a progress bar
// for the auto-dismiss. Tap to dismiss. Respects prefers-reduced-motion.
import React, { useContext, useEffect, useRef } from "react";
import axios from "axios";
import GameDataContext from "../../Context/GameDataContext";
import { getAccessToken } from "../Utils/TokenRefresh";
import { flyRewards } from "../Ui/rewardFlightStore";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useAchievementToast, AchievementUnlock } from "../../Context/AchievementToastContext";
import Portal from "../Utils/Portal";
import Icon, { IconName } from "../Ui/Icon";

const AUTO_DISMISS_MS = 4500;

// framer-motion 11 types AnimatePresence in a way @types/react 18 rejects as a
// JSX component; same cast as Frames/FrameItems.tsx.
const Presence = AnimatePresence as unknown as React.FC<{ children?: React.ReactNode; initial?: boolean }>;

const CATEGORY_ICON: Record<string, IconName> = {
  merge: "merge",
  producer: "hammer",
  quiz: "book",
  creature: "paw",
  economy: "coin",
  roleplay: "smile",
  daily: "fire",
};

const SPARKS = [
  { x: -118, y: -18, d: 0.05, s: 10 },
  { x: -96, y: 30, d: 0.18, s: 7 },
  { x: 120, y: -22, d: 0.1, s: 9 },
  { x: 104, y: 28, d: 0.26, s: 6 },
  { x: -20, y: -40, d: 0.32, s: 6 },
  { x: 40, y: 42, d: 0.22, s: 8 },
];

function rewardsOf(a: AchievementUnlock): Array<{ kind: "coins" | "moonrocks"; amount: number }> {
  if (a.rewards) {
    const out: Array<{ kind: "coins" | "moonrocks"; amount: number }> = [];
    if (a.rewards.coins > 0) out.push({ kind: "coins", amount: a.rewards.coins });
    if (a.rewards.moonrocks > 0) out.push({ kind: "moonrocks", amount: a.rewards.moonrocks });
    return out;
  }
  return a.rewardAmount > 0 ? [{ kind: a.rewardType, amount: a.rewardAmount }] : [];
}

const AchievementToast: React.FC = () => {
  const { current, dismiss } = useAchievementToast();
  const reduce = useReducedMotion();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const game = useContext(GameDataContext);
  const gameRef = useRef(game);
  gameRef.current = game;
  const paidRef = useRef<Set<string>>(new Set());

  // The server already paid the reward on unlock; sync the balance and fly
  // it from the reward pill to the HUD chips once the card has landed.
  useEffect(() => {
    if (!current || paidRef.current.has(current.id)) return undefined;
    const rewards = rewardsOf(current);
    if (!rewards.length) return undefined;
    paidRef.current.add(current.id);
    let cancelled = false;
    const t = window.setTimeout(async () => {
      let balances: { coinCount?: number; moonrockCount?: number } | null = null;
      try {
        const token = await getAccessToken();
        const r = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/moonrocks`, { headers: { Authorization: `Bearer ${token}` } });
        balances = r.data || null;
      } catch {
        balances = null;
      }
      if (cancelled) return;
      const g = gameRef.current;
      for (const rw of rewards) {
        const from = document.querySelector(`.ach-pop__reward--${rw.kind}`);
        const balance = rw.kind === "coins" ? balances?.coinCount : balances?.moonrockCount;
        const shown = rw.kind === "coins" ? g?.coinCount : g?.moonrockCount;
        // Only fly what the HUD does not show yet (a sync may have beaten us).
        const amount = typeof balance === "number" && typeof shown === "number" ? Math.min(rw.amount, balance - shown) : 0;
        if (amount > 0) flyRewards({ kind: rw.kind, amount, from });
        if (typeof balance === "number") {
          if (rw.kind === "coins") g?.setCoinCount(balance);
          else g?.setMoonrockCount(balance);
        }
      }
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [current]);

  useEffect(() => {
    if (!current) return;
    timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, dismiss]);

  const icon: IconName = (current?.category && CATEGORY_ICON[current.category]) || "trophy";
  const rewards = current ? rewardsOf(current) : [];

  return (
    <Portal>
      <Presence>
        {current && (
          <motion.div
            key={current.id}
            className="ach-pop"
            role="status"
            aria-live="polite"
            onClick={dismiss}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -40, scale: 0.9 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -24, scale: 0.96 }}
            transition={reduce ? { duration: 0.2 } : { type: "spring", stiffness: 420, damping: 26 }}
          >
            <div className="ach-pop__card">
              {!reduce && <span className="ach-pop__shine" aria-hidden="true" />}

              <div className="ach-pop__medal" aria-hidden="true">
                {!reduce && <span className="ach-pop__rays" />}
                <motion.span
                  className="ach-pop__coin"
                  initial={reduce ? false : { scale: 0.2, rotate: -40 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 380, damping: 14, delay: 0.08 }}
                >
                  <Icon name={icon} size={26} />
                </motion.span>
                {!reduce &&
                  SPARKS.map((sp, i) => (
                    <motion.span
                      key={i}
                      className="ach-pop__spark"
                      style={{ width: sp.s, height: sp.s }}
                      initial={{ opacity: 0, x: 0, y: 0, scale: 0.4 }}
                      animate={{ opacity: [0, 1, 0], x: sp.x * 0.45, y: sp.y * 0.9, scale: [0.4, 1, 0.6] }}
                      transition={{ duration: 0.9, delay: 0.2 + sp.d, ease: "easeOut" }}
                    />
                  ))}
              </div>

              <div className="ach-pop__text">
                <div className="ach-pop__eyebrow">
                  <Icon name="trophy" size={12} /> Achievement unlocked
                </div>
                <div className="ach-pop__name">{current.name || "New achievement"}</div>
                {current.description && <div className="ach-pop__desc">{current.description}</div>}
              </div>

              {rewards.length > 0 && (
                <div className="ach-pop__rewards">
                  {rewards.map((r, i) => (
                    <motion.span
                      key={r.kind}
                      className={`ach-pop__reward ach-pop__reward--${r.kind}`}
                      initial={reduce ? false : { scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 16, delay: 0.35 + i * 0.12 }}
                      aria-label={`plus ${r.amount} ${r.kind}`}
                    >
                      <Icon name={r.kind === "moonrocks" ? "moonrock" : "coin"} size={14} filled />+{r.amount}
                    </motion.span>
                  ))}
                </div>
              )}

              <span className="ach-pop__timer" style={{ animationDuration: `${AUTO_DISMISS_MS}ms` }} aria-hidden="true" />
            </div>
          </motion.div>
        )}
      </Presence>
    </Portal>
  );
};

export default AchievementToast;
